import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowLeft, ArrowRight, Check, CreditCard, Landmark, Lock, RefreshCw, CalendarClock } from 'lucide-react'
import { PageHeader, Panel, Button, Banner, useToast } from '../../ds/index'
import { useApi } from '../../lib/useApi'
import { post, patch } from '../../lib/api'
import { money } from '../../lib/format'
import { Icon } from '../../components/Icon'
import { LoadingBlock, ErrorBlock } from '../../components/Loading'
import { brandName, type Billing } from './ClientBilling'
import { creditFor100, type Summary } from './ClientOverview'
import { useCheckout, type CheckoutStart } from '../../lib/checkout'

type Step = 'card' | 'monthly' | 'refill' | 'done'
export type Method = 'card' | 'bank'
const STEPS: { key: Step; label: string; icon: typeof CreditCard }[] = [
  { key: 'card', label: 'How you pay', icon: CreditCard },
  { key: 'monthly', label: 'Monthly payment', icon: CalendarClock },
  { key: 'refill', label: 'Auto-refill', icon: RefreshCw },
  { key: 'done', label: 'Done', icon: Check },
]

/** Credit a cash amount buys at the prepaid discount. */
export const creditOf = (cents: number, bps: number) => Math.round(cents / (1 - bps / 10000))

/** What a method is called, as the client sees it. */
export const methodName = (b: Billing, m: Method) => (m === 'bank'
  ? (b.bank ? `${b.bank.name || 'bank account'} ending ${b.bank.last4}` : 'bank account')
  : (b.card ? `${brandName(b.card.brand)} ending ${b.card.last4}` : 'card'))

/**
 * Set up payments, one step at a time: how you pay (card or bank account), the
 * monthly payment, auto-refill. Each step says exactly what will be charged,
 * when, and from what. Every step but the first can be skipped, and everything
 * can be changed later on Billing.
 */
export default function ClientPaymentSetup() {
  const b = useApi<Billing>('/api/client/billing')
  const s = useApi<Summary>('/api/client/summary')
  const [params, setParams] = useSearchParams()
  // Back from Stripe: which method was being added, and how it went.
  const [returned] = useState(() => ({ card: params.get('card'), bank: params.get('bank') }))
  const [step, setStep] = useState<Step | null>(null)
  const [method, setMethod] = useState<Method | null>(returned.bank === 'saved' ? 'bank' : returned.card === 'saved' ? 'card' : null)
  const [waiting, setWaiting] = useState(returned.card === 'saved' || returned.bank === 'saved')

  useEffect(() => { if (params.get('card') || params.get('bank')) setParams({}, { replace: true }) }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Back from Stripe with a card or bank account: it is saved when Stripe's message arrives, usually in seconds. Look again until it shows.
  useEffect(() => {
    if (!waiting || !b.data) return
    if (method === 'bank' ? b.data.bank : b.data.card) { setWaiting(false); setStep('monthly'); return }
    const t = setTimeout(b.reload, 2000)
    const stop = setTimeout(() => setWaiting(false), 30000)
    return () => { clearTimeout(t); clearTimeout(stop) }
  }, [waiting, b.data]) // eslint-disable-line react-hooks/exhaustive-deps

  // Start at the first thing not done yet.
  useEffect(() => {
    if (step || !b.data || waiting) return
    const d = b.data
    if (!method) setMethod(d.autopay ? d.monthly_method : d.bank && !d.card ? 'bank' : 'card')
    setStep(!d.card && !d.bank ? 'card' : !d.autopay ? 'monthly' : !d.refill.enabled ? 'refill' : 'done')
  }, [b.data, waiting]) // eslint-disable-line react-hooks/exhaustive-deps

  if (b.error) return <ErrorBlock error={b.error} />
  if (!b.data || !s.data) return <LoadingBlock />
  const d = b.data
  const bps = s.data.wallet.discount_bps
  const reload = () => { b.reload(); s.reload() }
  const hasAny = Boolean(d.card || d.bank)
  const chosen: Method = method && (method === 'bank' ? d.bank : d.card) ? method : d.card ? 'card' : 'bank'

  return (
    <>
      <div style={{ marginBottom: 'var(--s-4)' }}><Link className="btn btn-ghost" to="/portal/billing"><Icon of={ArrowLeft} /> Billing</Link></div>
      <PageHeader title="Set up payments" subtitle={`Three short steps, by card or bank account. Payments go to ${d.org.name} through Stripe, and you can change any of it later.`} />

      {!d.can_pay_online ? (
        <Banner tone="warn" title="Paying online is not set up yet">Contact {d.org.name} to add money or pay a bill.</Banner>
      ) : (
        <div className="setup-flow">
          <ol className="setup-rail" aria-label="Steps">
            {STEPS.map((x, i) => {
              const done = x.key === 'card' ? hasAny : x.key === 'monthly' ? d.autopay : x.key === 'refill' ? d.refill.enabled : false
              const on = step === x.key
              return (
                <li key={x.key} className={`${on ? 'is-on' : ''}${done ? ' is-done' : ''}`}>
                  <button type="button" onClick={() => setStep(x.key)} disabled={x.key !== 'card' && !hasAny} aria-current={on ? 'step' : undefined} aria-label={`Step ${i + 1}: ${x.label}${done ? ', done' : ''}`}>
                    <span className="setup-rail-dot">{done ? <Icon of={Check} size={14} /> : i + 1}</span>
                    <span>{x.label}</span>
                  </button>
                </li>
              )
            })}
          </ol>

          {waiting && (
            <Panel><div className="setup-wait"><span className="spinner" aria-hidden="true" /><div><strong>Saving your {method === 'bank' ? 'bank account' : 'card'}</strong><span className="muted">Stripe is confirming it. This takes a few seconds.</span></div></div></Panel>
          )}
          {(returned.card === 'cancelled' || returned.bank === 'cancelled') && step === 'card' && <Banner tone="warn" title="Nothing saved">You closed the form. Nothing was charged.</Banner>}
          {!waiting && step === 'card' && <MethodStep billing={d} method={chosen} onUse={(m) => { setMethod(m); setStep('monthly') }} onAdding={setMethod} />}
          {!waiting && step === 'monthly' && <MonthlyStep billing={d} bps={bps} method={chosen} onDone={() => { reload(); setStep('refill') }} onSkip={() => setStep('refill')} />}
          {!waiting && step === 'refill' && <RefillStep billing={d} bps={bps} preferred={chosen} onDone={() => { reload(); setStep('done') }} onSkip={() => setStep('done')} />}
          {!waiting && step === 'done' && <DoneStep billing={d} bps={bps} />}
        </div>
      )}
    </>
  )
}

/**
 * How you pay: a card (instant) or a bank account (lower cost for the company,
 * 3 to 5 business days to clear). Either is saved on Stripe's own form, inside
 * the portal; nothing is charged to save it.
 */
export function MethodStep({ billing, method, onUse, onAdding, returnTo = 'setup' }: { billing: Billing; method: Method; onUse: (m: Method) => void; onAdding: (m: Method) => void; returnTo?: 'setup' | 'welcome' }) {
  const toast = useToast()
  const [busy, setBusy] = useState(false)
  const [pick, setPick] = useState<Method>(method)
  const cardForm = useCheckout(billing.card ? 'Use a different card' : 'Add a card')
  const bankForm = useCheckout(billing.bank ? 'Use a different bank account' : 'Add a bank account')
  const add = async (m: Method) => {
    setBusy(true); onAdding(m)
    try { (m === 'card' ? cardForm : bankForm).open(await post<CheckoutStart>(`/api/client/${m}`, { return_to: returnTo }), () => setBusy(false)) }
    catch (e) { toast((e as Error).message, 'neg'); setBusy(false) }
  }
  const saved = pick === 'bank' ? billing.bank : billing.card
  const options: { m: Method; icon: typeof CreditCard; title: string; points: string }[] = [
    { m: 'card', icon: CreditCard, title: 'Card', points: 'Instant. Credit is added as soon as you pay.' },
    { m: 'bank', icon: Landmark, title: 'Bank account', points: 'Straight from your bank. Takes 3 to 5 business days to clear.' },
  ]
  return (
    <Panel>
      <div className="setup-step-head">
        <span className="setup-step-icon"><Icon of={Lock} size={20} /></span>
        <div><h2>How you will pay</h2><p className="muted">Saved securely by Stripe. {billing.org.name} and SPEC never see your card or account number. You can add the other one later.</p></div>
      </div>
      <div className="method-picks" role="radiogroup" aria-label="How you will pay">
        {options.map((o) => {
          const on = o.m === 'bank' ? billing.bank : billing.card
          return (
            <button key={o.m} type="button" role="radio" aria-checked={pick === o.m} className="method-pick" onClick={() => setPick(o.m)}>
              <span className="method-pick-icon"><Icon of={o.icon} size={20} /></span>
              <strong>{o.title}</strong>
              <span className="muted">{on ? `Saved: ${methodName(billing, o.m)}` : o.points}</span>
            </button>
          )
        })}
      </div>
      <div className="setup-actions">
        {saved ? (
          <>
            <Button onClick={() => onUse(pick)}>Use {methodName(billing, pick)} <Icon of={ArrowRight} size={15} /></Button>
            <Button variant="ghost" onClick={() => add(pick)} disabled={busy}>Use a different {pick === 'bank' ? 'bank account' : 'card'}</Button>
          </>
        ) : (
          <Button onClick={() => add(pick)} disabled={busy}><Icon of={Lock} size={15} /> {busy ? 'Opening Stripe...' : pick === 'bank' ? 'Connect your bank account' : 'Add a card'}</Button>
        )}
      </div>
      <p className="muted setup-foot">{pick === 'bank' ? 'You sign in to your bank on Stripe\'s form to connect the account. ' : ''}Nothing is charged when you save it.</p>
      {cardForm.element}{bankForm.element}
    </Panel>
  )
}

function MonthlyStep({ billing, bps, method, onDone, onSkip }: { billing: Billing; bps: number; method: Method; onDone: () => void; onSkip: () => void }) {
  const toast = useToast()
  const min = billing.org.monthly_minimum_cents
  const choices = [...new Set([min, min * 2, min * 3, min * 5].filter((c) => c >= 1000))]
  const [cents, setCents] = useState(Math.max(min, billing.monthly_amount_cents || min))
  const [custom, setCustom] = useState('')
  const [busy, setBusy] = useState(false)
  const amount = custom ? Math.round(Number(custom) * 100) : cents
  const valid = Number.isInteger(amount) && amount >= min

  const turnOn = async () => {
    if (!valid) return toast(`The monthly payment is at least ${money(min)}`, 'neg')
    setBusy(true)
    try {
      await patch('/api/client/payment-settings', { monthly_amount_cents: amount })
      await patch('/api/client/autopay', { on: true, method })
      toast('Monthly payment on', 'pos')
      onDone()
    } catch (e) { toast((e as Error).message, 'neg') } finally { setBusy(false) }
  }

  return (
    <Panel>
      <div className="setup-step-head">
        <span className="setup-step-icon"><Icon of={CalendarClock} size={20} /></span>
        <div><h2>Monthly payment</h2><p className="muted">A set amount each month, added as lead credit at {bps / 100}% off. It also pays any bill for leads past your credit.</p></div>
      </div>
      <div className="amount-picks" role="radiogroup" aria-label="Monthly amount">
        {choices.map((c) => (
          <button key={c} type="button" role="radio" aria-checked={!custom && cents === c} aria-label={`${money(c)} a month, ${money(creditOf(c, bps))} of credit`} className="amount-pick" onClick={() => { setCents(c); setCustom('') }}>
            <strong>{money(c)}</strong><span>{money(creditOf(c, bps))} of credit</span>{c === min && <em>Minimum</em>}
          </button>
        ))}
        <label className={`amount-pick is-custom${custom ? ' is-on' : ''}`}>
          <span className="muted">Other</span>
          <span className="amount-input">$<input inputMode="decimal" value={custom} placeholder={(min / 100).toString()} onChange={(e) => setCustom(e.target.value.replace(/[^0-9.]/g, ''))} aria-label="Other monthly amount, dollars" /></span>
        </label>
      </div>
      <div className="setup-summary">
        {valid
          ? <><strong>{money(amount)} today</strong> (unless this month's is already paid), then {money(amount)} on the 1st of every month, from your {methodName(billing, method)}. Each time adds {money(creditOf(amount, bps))} of lead credit{method === 'bank' ? ', once the payment clears (3 to 5 business days)' : ''}.</>
          : <>The monthly payment is at least {money(min)}.</>}
      </div>
      <div className="setup-actions">
        <Button onClick={turnOn} disabled={busy || !valid}>{busy ? 'Turning on...' : `Turn on ${valid ? money(amount) : ''} a month`}</Button>
        <Button variant="ghost" onClick={onSkip}>Not now</Button>
      </div>
      <p className="muted setup-foot">Stop it any time on Billing. Without it, add money yourself to keep the {bps / 100}% discount.</p>
    </Panel>
  )
}

const BELOW = [5000, 10000, 20000]
const ADD = [10000, 20000, 30000]
const CAP = [50000, 100000, 200000]

function RefillStep({ billing, bps, preferred, onDone, onSkip }: { billing: Billing; bps: number; preferred: Method; onDone: () => void; onSkip: () => void }) {
  const toast = useToast()
  const r = billing.refill
  // A card refills at once, so it is the better fit here when there is one.
  const [method, setMethod] = useState<Method>(r.enabled ? billing.refill_method : billing.card ? 'card' : preferred)
  const [below, setBelow] = useState(r.threshold_cents ?? 10000)
  const [add, setAdd] = useState(r.amount_cents ?? 20000)
  const [cap, setCap] = useState(r.monthly_max_cents ?? 100000)
  const [busy, setBusy] = useState(false)
  const capOk = add <= cap

  const turnOn = async () => {
    setBusy(true)
    try {
      await patch('/api/client/payment-settings', { refill_enabled: true, refill_method: method, refill_threshold_cents: below, refill_amount_cents: add, refill_monthly_max_cents: cap })
      toast('Auto-refill on', 'pos')
      onDone()
    } catch (e) { toast((e as Error).message, 'neg') } finally { setBusy(false) }
  }
  const Pick = ({ value, options, onPick, label }: { value: number; options: number[]; onPick: (v: number) => void; label: string }) => (
    <div className="chip-pick" role="radiogroup" aria-label={label}>
      {options.map((o) => <button key={o} type="button" role="radio" aria-checked={value === o} aria-label={`${label}: ${money(o)}`} className="filter-chip" onClick={() => onPick(o)}>{money(o)}</button>)}
    </div>
  )

  return (
    <Panel>
      <div className="setup-step-head">
        <span className="setup-step-icon"><Icon of={RefreshCw} size={20} /></span>
        <div><h2>Auto-refill <span className="muted" style={{ fontWeight: 400 }}>(optional)</span></h2><p className="muted">When your credit gets low, top it up at {bps / 100}% off, so busy weeks never go on the bill at full price.</p></div>
      </div>
      {billing.card && billing.bank && (
        <div className="refill-method">
          <span className="field-label">Pay refills with</span>
          <div className="chip-pick" role="radiogroup" aria-label="Pay refills with">
            {(['card', 'bank'] as Method[]).map((m) => <button key={m} type="button" role="radio" aria-checked={method === m} className="filter-chip" onClick={() => setMethod(m)}>{methodName(billing, m)}</button>)}
          </div>
          {method === 'bank' && <p className="muted" style={{ margin: 0, fontSize: 13 }}>A bank refill arrives 3 to 5 business days later. Card is quicker for this one.</p>}
        </div>
      )}
      <div className="refill-grid">
        <div><span className="field-label">When credit falls below</span><Pick value={below} options={BELOW} onPick={setBelow} label="When credit falls below" /></div>
        <div><span className="field-label">Add</span><Pick value={add} options={ADD} onPick={setAdd} label="Add" /></div>
        <div><span className="field-label">Never more than, a month</span><Pick value={cap} options={CAP} onPick={setCap} label="Monthly limit" /></div>
      </div>
      <div className="setup-summary">
        {capOk
          ? <>When your credit drops under <strong>{money(below)}</strong>, we add <strong>{money(add)}</strong> ({money(creditOf(add, bps))} of credit) from your {methodName(billing, method)}, at most <strong>{money(cap)}</strong> in a month.</>
          : <>The monthly limit has to be at least the refill amount.</>}
      </div>
      <div className="setup-actions">
        <Button onClick={turnOn} disabled={busy || !capOk}>{busy ? 'Turning on...' : 'Turn on auto-refill'}</Button>
        <Button variant="ghost" onClick={onSkip}>Skip</Button>
      </div>
    </Panel>
  )
}

function DoneStep({ billing, bps }: { billing: Billing; bps: number }) {
  const go = useNavigate()
  const r = billing.refill
  return (
    <Panel>
      <div className="setup-step-head">
        <span className="setup-step-icon is-done"><Icon of={Check} size={20} /></span>
        <div><h2>You are set up</h2><p className="muted">Here is what happens from now on. Change any of it on Billing.</p></div>
      </div>
      <ul className="setup-recap">
        <li><Icon of={CreditCard} size={16} /><span>Card: <strong>{billing.card ? methodName(billing, 'card') : 'none saved'}</strong></span></li>
        <li><Icon of={Landmark} size={16} /><span>Bank account: <strong>{billing.bank ? methodName(billing, 'bank') : 'none saved'}</strong></span></li>
        <li><Icon of={CalendarClock} size={16} /><span>Monthly payment: <strong>{billing.autopay ? `${money(Math.max(billing.monthly_amount_cents, billing.org.monthly_minimum_cents))} on the 1st` : 'off'}</strong>{billing.autopay ? ` from your ${methodName(billing, billing.monthly_method)}, ${money(creditOf(Math.max(billing.monthly_amount_cents, billing.org.monthly_minimum_cents), bps))} of credit each time` : ', add money yourself to keep the discount'}</span></li>
        <li><Icon of={RefreshCw} size={16} /><span>Auto-refill: <strong>{r.enabled ? `add ${money(r.amount_cents || 0)} under ${money(r.threshold_cents || 0)}` : 'off'}</strong>{r.enabled ? ` from your ${methodName(billing, billing.refill_method)}, at most ${money(r.monthly_max_cents || 0)} a month` : ''}</span></li>
        <li><Icon of={Check} size={16} /><span>Leads keep coming either way. Your credit is used first, at {bps / 100}% off; with none left, a lead goes on the bill at full price.</span></li>
      </ul>
      <div className="setup-actions">
        <Button onClick={() => go('/portal')}>Go to your overview</Button>
        <Link className="btn btn-secondary" to="/portal/billing">Add money now</Link>
      </div>
      <p className="muted setup-foot">{money(10000)} added up front buys {money(creditFor100(bps))} of leads.</p>
    </Panel>
  )
}
